/**
 * Utility functions for checking user roles and permissions
 */

interface Role {
  role_id: number;
  role_name: string;
}

interface FirmContext {
  roleName?: string;
}

/**
 * Check if the user is an admin or super admin
 * @param firmContext - The firm context object containing roleName
 * @returns True if user is admin or super_admin
 */
export const isAdmin = (firmContext: FirmContext | null | undefined): boolean => {
  return firmContext?.roleName === "admin" || firmContext?.roleName === "super_admin";
};

/**
 * Check if the user is a professional
 * @param firmContext - The firm context object containing roleName
 * @returns True if user is a professional
 */
export const isProfessional = (firmContext: FirmContext | null | undefined): boolean => {
  return firmContext?.roleName === "professional";
};

/**
 * Filter required roles from all roles
 * @param roles - Array of role objects with role_name property
 * @param reqRoleNames - Array of required role names to filter (checks both exact match and substring)
 * @returns Filtered array of roles matching the required role names
 */
export const filterReqRolesFromAll = (roles: Role[], reqRoleNames: string[]): Role[] => {
  if (!reqRoleNames || reqRoleNames.length === 0) {
    return roles;
  }
  return roles.filter((r) => {
    return reqRoleNames.some((reqName) => r.role_name === reqName);
  });
};

/**
 * Get the default assignable role ID (first role that is not super and not physician)
 * @param roles - Array of role objects with role_name and role_id properties
 * @param fromRoles - Array of required role names to filter (checks both exact match and substring)
 * @returns The default role_id as string or empty string if none found
 */
export const getDefaultAssignableRoleId = (roles: Role[], fromRoles: string[]): string => {
  const filteredRoles = filterReqRolesFromAll(roles, fromRoles);
  return filteredRoles[0]?.role_id.toString() || "";
};
